import React, { useRef } from 'react';
import { useFocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import styles from './CarouselDots.module.css';
import { focusStyleVariants } from '../../../utils/focus/focusStyles';

export interface CarouselDotsProps {
  totalItems: number;
  currentPage: number;
  onDotClick: (index: number) => void;
  dotsSize?: 'md' | 'lg';
  variant?: 'standard' | 'transparent';
  focusStyle?: 'default' | 'white';
  className?: string;
  dotsWrapperClassName?: string;
  dotClassName?: string;
  activeDotClassName?: string;
  isTransitioning?: boolean;
  /**
   * Callback fired when any dot is pressed
   */
  onPress?: (index: number) => void;
}

export const CarouselDots: React.FC<CarouselDotsProps> = ({
  totalItems,
  currentPage,
  onDotClick,
  dotsSize = 'md',
  variant = 'standard',
  focusStyle = 'default',
  className = '',
  dotsWrapperClassName = '',
  dotClassName = '',
  activeDotClassName = '',
  isTransitioning = false,
  onPress,
}) => {
  const tabsRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Add React Aria hooks for focus and hover states (container only)
  const { isFocusVisible, focusProps } = useFocusRing();
  const { isHovered, hoverProps } = useHover({});

  // Add keyboard navigation functionality
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isTransitioning) return; // Don't allow navigation during transitions

    let handled = false;

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      const nextPage = (currentPage + 1) % totalItems;
      onDotClick(nextPage);
      handled = true;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      const prevPage = (currentPage - 1 + totalItems) % totalItems;
      onDotClick(prevPage);
      handled = true;
    } else if (e.key === 'Home') {
      e.preventDefault();
      onDotClick(0);
      handled = true;
    } else if (e.key === 'End') {
      e.preventDefault();
      onDotClick(totalItems - 1);
      handled = true;
    } else if (e.key === ' ' || e.key === 'Enter') {
      // Space and Enter don't change slides, but we prevent default to avoid scrolling
      e.preventDefault();
      handled = true;
    }

    // If we handled the key, ensure focus remains on the dots container
    if (handled && tabsRef.current) {
      tabsRef.current.focus();
    }
  };

  // Get CSS classes for container
  const getContainerClasses = () => {
    const classes = [styles.tabsContainer];
    if (className) classes.push(className);
    if (isTransitioning) classes.push(styles.transitioning);
    return classes.join(' ');
  };

  // Get CSS classes for dots wrapper
  const getDotsWrapperClasses = () => {
    const classes = [styles.tabs];

    if (variant === 'transparent') classes.push(styles.transparent);

    // Add focus style variants consistent with other components
    classes.push(focusStyleVariants({ focusStyle }));

    if (dotsWrapperClassName) classes.push(dotsWrapperClassName);

    return classes.join(' ');
  };

  const handleDotClick = (index: number) => {
    // Prevent click during transitions
    if (isTransitioning) return;

    onDotClick(index);
    if (onPress) {
      onPress(index);
    }

    // Defer focus to run after the browser's default focus behavior
    requestAnimationFrame(() => {
      if (tabsRef.current) {
        tabsRef.current.focus();
      }
    });
  };

  // Handle individual dot keyboard events
  const handleDotKeyDown = (e: React.KeyboardEvent, index: number) => {
    // Prevent keyboard events during transitions
    if (isTransitioning) return;

    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation(); // Prevent bubbling to container
      onDotClick(index);
    }
  };

  // Get aria-label for individual dots
  const getDotAriaLabel = (index: number) => {
    const slideNumber = index + 1;
    const isCurrent = index === currentPage;
    return `Go to slide ${slideNumber}${isCurrent ? ', current slide' : ''}`;
  };

  // Get aria-label for the dots container
  const getContainerAriaLabel = () => {
    return `Slide pagination, ${totalItems} slides, currently on slide ${currentPage + 1}`;
  };

  return (
    <div
      className={getContainerClasses()}
      ref={wrapperRef}
      data-dot-size={dotsSize}
      data-transitioning={isTransitioning ? 'true' : undefined}
    >
      <div
        className={getDotsWrapperClasses()}
        ref={tabsRef}
        tabIndex={0}
        role="tablist"
        aria-label={getContainerAriaLabel()}
        onKeyDown={handleKeyDown}
        data-focused={isFocusVisible ? true : undefined}
        data-focus-visible={isFocusVisible ? true : undefined}
        data-hovered={isHovered ? true : undefined}
        aria-activedescendant={`dot-${currentPage}`}
        {...focusProps}
        {...hoverProps}
      >
        <div className={styles.dotsScroller}>
          {Array.from({ length: totalItems }, (_, index) => {
            const isActive = index === currentPage;

            // Generate fixed styles for consistent appearance
            const baseStyle = {
              width: dotsSize === 'lg' ? '12px' : '8px',
              height: dotsSize === 'lg' ? '12px' : '8px',
              borderRadius: '50%',
              backgroundColor: isActive
                ? 'var(--alto-sem-color-fg-primary)'
                : 'rgba(0, 0, 0, 0.3)',
              border: 'none',
              padding: 0,
              cursor: isTransitioning ? 'default' : 'pointer',
              display: 'block',
              flexShrink: 0,
              margin: 0,
              transition: 'background-color 0.2s ease',
            };

            return (
              <button
                key={index}
                id={`dot-${index}`}
                type="button"
                className={`${styles.tab} ${isActive ? styles.activeTab : ''} ${dotClassName || ''} ${isActive && activeDotClassName ? activeDotClassName : ''}`}
                onClick={() => handleDotClick(index)}
                onKeyDown={(e) => handleDotKeyDown(e, index)}
                aria-label={getDotAriaLabel(index)}
                aria-selected={isActive}
                aria-current={isActive ? 'true' : 'false'}
                role="tab"
                data-tab-index={index}
                tabIndex={-1} // Individual dots are not focusable, container handles focus
                style={baseStyle}
                disabled={isTransitioning} // Disable dots during transitions
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CarouselDots;
